import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const AboutPage = () => {
  const values = [
    {
      icon: 'ri-shield-check-line',
      title: 'Guaranteed Quality',
      description: 'Absolute commitment to excellence in every project we undertake'
    },
    {
      icon: 'ri-team-line',
      title: 'Professional Team',
      description: 'Highly qualified personnel with years of experience in the industry'
    },
    {
      icon: 'ri-time-line',
      title: 'Punctuality',
      description: 'Strict adherence to deadlines without compromising quality'
    },
    {
      icon: 'ri-customer-service-line',
      title: 'Personalized Service',
      description: 'Close monitoring and constant communication throughout the project'
    },
    {
      icon: 'ri-leaf-line',
      title: 'Sustainability',
      description: 'Use of environmentally friendly materials and techniques'
    },
    {
      icon: 'ri-price-tag-3-line',
      title: 'Transparent Pricing',
      description: 'Clear and detailed quotes with no hidden costs'
    }
  ];

  const stats = [
    { number: '15+', label: 'Years of Experience' },
    { number: '500+', label: 'Completed Projects' },
    { number: '98%', label: 'Satisfied Clients' },
    { number: '25+', label: 'Professionals' }
  ];

  const team = [
    {
      name: 'Carlos Martínez',
      role: 'General Director',
      image: 'https://public.readdy.ai/ai/img_res/a915d97ada5b3b647db93c2a1d953379.jpg'
    },
    {
      name: 'Ana García',
      role: 'Project Manager',
      image: 'https://readdy.ai/api/search-image?query=professional%20female%20construction%20project%20manager%20in%20safety%20vest%20and%20hard%20hat%20reviewing%20building%20plans%20at%20construction%20site%20showing%20confidence%20and%20expertise%20in%20modern%20construction%20environment&width=600&height=800&seq=team002&orientation=portrait'
    },
    {
      name: 'Miguel Rodríguez',
      role: 'Master Carpenter',
      image: 'https://readdy.ai/api/search-image?query=experienced%20master%20carpenter%20in%20work%20clothes%20examining%20quality%20wooden%20materials%20in%20professional%20workshop%20showing%20craftsmanship%20and%20expertise%20with%20tools%20and%20lumber%20in%20organized%20workspace&width=600&height=800&seq=team003&orientation=portrait'
    },
    {
      name: 'Laura Sánchez',
      role: 'Safety Coordinator',
      image: 'https://readdy.ai/api/search-image?query=professional%20female%20safety%20coordinator%20in%20high%20visibility%20vest%20and%20hard%20hat%20inspecting%20construction%20site%20with%20clipboard%20showing%20attention%20to%20safety%20protocols%20in%20active%20building%20environment&width=600&height=800&seq=team004&orientation=portrait'
    }
  ];

  const certifications = [
    'ISO 9001 - Quality Management',
    'ISO 14001 - Environmental Management',
    'OHSAS 18001 - Health & Safety',
    'Energy Efficiency Certification',
    'Accredited Companies Registry',
    'Liability Insurance'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden mt-20">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20construction%20company%20team%20standing%20together%20at%20modern%20building%20site%20wearing%20safety%20equipment%20and%20hard%20hats%20showing%20unity%20teamwork%20and%20expertise%20with%20completed%20quality%20construction%20project%20in%20background&width=1920&height=800&seq=abouthero001&orientation=landscape"
            alt="About Us"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-sm font-bold tracking-widest text-orange uppercase mb-4">
              ABOUT US
            </p>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              Building Trust Since 2008
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Over 15 years of experience in professional construction and rehabilitation
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Story Section */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <p className="text-sm font-bold tracking-widest text-orange uppercase">
                OUR STORY
              </p>
              <h2 className="text-4xl lg:text-5xl font-bold text-charcoal">
                Experience and Commitment
              </h2>
              <p className="text-lg text-textGrey leading-relaxed">
                LEVANTINA was founded in 2008 with a clear vision: to offer the highest quality construction and rehabilitation services, combining traditional techniques with the latest industry innovations.
              </p>
              <p className="text-lg text-textGrey leading-relaxed">
                Over these years, we have completed more than 500 projects, from small renovations to large construction works, always maintaining our commitment to excellence, punctuality, and client satisfaction.
              </p>
              <p className="text-lg text-textGrey leading-relaxed">
                Our team consists of highly qualified professionals who share our passion for work well done. Each project is unique, and we approach it with the same dedication and attention to detail.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://readdy.ai/api/search-image?query=modern%20construction%20company%20office%20interior%20with%20architectural%20blueprints%20construction%20models%20and%20professional%20team%20collaborating%20around%20table%20with%20building%20plans%20showing%20organized%20professional%20work%20environment&width=800&height=1000&seq=story001&orientation=portrait"
                alt="Our company"
                className="w-full h-full object-cover object-top rounded-lg shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-charcoal py-20 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-5xl lg:text-6xl font-bold text-orange mb-3">
                  {stat.number}
                </div>
                <div className="text-white text-lg font-medium">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm font-bold tracking-widest text-orange uppercase mb-4">
              OUR VALUES
            </p>
            <h2 className="text-4xl lg:text-5xl font-bold text-charcoal mb-6">
              What Defines Us
            </h2>
            <p className="text-lg text-textGrey max-w-3xl mx-auto">
              Fundamental principles that guide each of our projects
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-concrete p-8 rounded-lg hover:shadow-xl transition-shadow"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-orange/10 rounded-lg mb-6">
                  <i className={`${value.icon} text-3xl text-orange`}></i>
                </div>
                <h3 className="text-xl font-bold text-charcoal mb-3">
                  {value.title}
                </h3>
                <p className="text-textGrey leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="bg-concrete py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm font-bold tracking-widest text-orange uppercase mb-4">
              OUR TEAM
            </p>
            <h2 className="text-4xl lg:text-5xl font-bold text-charcoal mb-6">
              Committed Professionals
            </h2>
            <p className="text-lg text-textGrey max-w-3xl mx-auto">
              Meet some of the professionals who make our projects possible
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                <div className="relative overflow-hidden rounded-lg mb-4 h-80">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>
                <h3 className="text-xl font-bold text-charcoal mb-1">
                  {member.name}
                </h3>
                <p className="text-orange font-medium">
                  {member.role}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <p className="text-sm font-bold tracking-widest text-orange uppercase">
                CERTIFICATIONS
              </p>
              <h2 className="text-4xl lg:text-5xl font-bold text-charcoal">
                Certified Quality
              </h2>
              <p className="text-lg text-textGrey leading-relaxed">
                We hold all necessary certifications and accreditations to guarantee maximum quality and safety in our projects.
              </p>
              <div className="space-y-4">
                {certifications.map((cert, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-orange/10 rounded flex-shrink-0">
                      <i className="ri-checkbox-circle-line text-orange text-xl"></i>
                    </div>
                    <span className="text-textGrey font-medium">{cert}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://readdy.ai/api/search-image?query=professional%20construction%20quality%20inspector%20reviewing%20building%20certifications%20and%20compliance%20documents%20with%20clipboard%20at%20modern%20construction%20site%20showing%20attention%20to%20standards%20and%20regulations%20in%20organized%20professional%20setting&width=800&height=1000&seq=cert001&orientation=portrait"
                alt="Certifications"
                className="w-full h-full object-cover object-top rounded-lg shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-charcoal py-24 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Want to Work with Us?
            </h2>
            <p className="text-xl text-white/90 mb-10">
              We're ready to bring your project to life
            </p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <a
                href="/#contact"
                className="inline-flex items-center justify-center space-x-2 bg-orange text-white px-8 py-4 font-semibold text-base tracking-wide hover:bg-orange/90 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span>REQUEST A QUOTE</span>
                <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href="/services"
                className="inline-flex items-center justify-center space-x-2 bg-transparent text-white px-8 py-4 font-semibold text-base tracking-wide border-2 border-white hover:bg-white hover:text-charcoal transition-all whitespace-nowrap cursor-pointer"
              >
                <span>VIEW SERVICES</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
